import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EventSummary } from './auth';
import { apiFetch } from './client';

export interface Event {
  id: string;
  slug: string;
  name: string;
  shortCode: string;
  venue: string | null;
  city: string | null;
  startsOn: string;
  endsOn: string;
  timezone: string;
  currency: string;
  stripeAccountId: string;
  stripeLocationId: string;
  allowCustomAmount: boolean;
  pin: string;
  allowOffhoursAuth: boolean;
  allowCashPayments: boolean;
  /** True iff a cash PIN is configured. The hash itself is never returned. */
  cashPinSet: boolean;
  internalNotes: string | null;
  publishedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Canonical Stripe acct_xxx for the linked stripe_accounts row. */
  stripeAccountStripeId: string | null;
  /** Derived from publishable-key prefix on the linked stripe_accounts row. */
  stripeMode: 'test' | 'live' | null;
}

export interface CreateEventBody {
  name: string;
  shortCode: string;
  startsOn: string;
  endsOn: string;
  timezone: string;
  stripeAccountId: string;
  stripeLocationId: string;
  pin: string;
  venue?: string | null;
  city?: string | null;
  currency?: string;
  allowCustomAmount?: boolean;
}

export interface PatchEventBody {
  name?: string;
  shortCode?: string;
  startsOn?: string;
  endsOn?: string;
  venue?: string | null;
  city?: string | null;
  timezone?: string;
  pin?: string;
  allowCustomAmount?: boolean;
  allowOffhoursAuth?: boolean;
  allowCashPayments?: boolean;
  /**
   * Virtual: 6-digit string sets a new cash PIN (server-side argon2id hash);
   * explicit null clears the hash (cash without a PIN); omitted leaves the
   * existing value untouched.
   */
  cashPin?: string | null;
  internalNotes?: string | null;
  /**
   * Switch the event to a different Stripe account. Must be sent together with
   * a fresh `stripeLocationId` (tml_xxx is bound to a single Stripe account).
   * Historical transactions stay tagged with their original stripeAccountId,
   * so refunds and reporting on those rows still hit the right account.
   */
  stripeAccountId?: string;
  stripeLocationId?: string;
}

export function useEvents(opts: { includeArchived?: boolean } = {}) {
  const qs = opts.includeArchived ? '?includeArchived=1' : '';
  return useQuery({
    queryKey: ['events', { includeArchived: opts.includeArchived ?? false }],
    queryFn: () =>
      apiFetch<{ events: EventSummary[] }>(`/api/v1/admin/events${qs}`).then((r) => r.events),
  });
}

export function useEvent(eventId: string) {
  return useQuery({
    queryKey: ['event', eventId],
    queryFn: () =>
      apiFetch<{ event: Event }>(`/api/v1/admin/events/${eventId}`).then((r) => r.event),
    enabled: Boolean(eventId),
  });
}

export function usePatchEvent(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchEventBody) =>
      apiFetch<{ event: Event }>(`/api/v1/admin/events/${eventId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.event),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event', eventId] });
      qc.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateEventBody) =>
      apiFetch<{ event: Event }>('/api/v1/admin/events', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.event),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useArchiveEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      apiFetch<{ event: Event }>(`/api/v1/admin/events/${eventId}/archive`, {
        method: 'POST',
      }).then((r) => r.event),
    onSuccess: (_data, eventId) => {
      qc.invalidateQueries({ queryKey: ['events'] });
      qc.invalidateQueries({ queryKey: ['event', eventId] });
    },
  });
}

export function usePublishEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      apiFetch<{ event: Event }>(`/api/v1/admin/events/${eventId}/publish`, {
        method: 'POST',
      }).then((r) => r.event),
    onSuccess: (_data, eventId) => {
      qc.invalidateQueries({ queryKey: ['events'] });
      qc.invalidateQueries({ queryKey: ['event', eventId] });
    },
  });
}

export function useUnpublishEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      apiFetch<{ event: Event }>(`/api/v1/admin/events/${eventId}/unpublish`, {
        method: 'POST',
      }).then((r) => r.event),
    onSuccess: (_data, eventId) => {
      qc.invalidateQueries({ queryKey: ['events'] });
      qc.invalidateQueries({ queryKey: ['event', eventId] });
    },
  });
}

export function useUnarchiveEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      apiFetch<{ event: Event }>(`/api/v1/admin/events/${eventId}/unarchive`, {
        method: 'POST',
      }).then((r) => r.event),
    onSuccess: (_data, eventId) => {
      qc.invalidateQueries({ queryKey: ['events'] });
      qc.invalidateQueries({ queryKey: ['event', eventId] });
    },
  });
}

/** Swap the event's short code for a fresh random 6-character one. */
export function useNewEventCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      apiFetch<{ event: Event }>(`/api/v1/admin/events/${eventId}/new-code`, { method: 'POST' }).then(
        (r) => r.event,
      ),
    // Write the returned event straight into the cache. Invalidating ['event', id]
    // would refetch, and the code would flicker back until the GET caught up.
    onSuccess: (event, eventId) => {
      qc.setQueryData(['event', eventId], event);
      qc.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

/**
 * Copy for the event-form errors the operator can fix in place. Shared by
 * EventCreate and EventSetup so both forms say the same thing.
 */
export const SHORT_CODE_TAKEN = 'That event code is already used by another active event.';
/** Shown under the field with SHORT_CODE_TAKEN. */
export const SHORT_CODE_TAKEN_HINT =
  "Archived events don't hold codes; unarchiving one checks again.";
export const WEAK_PIN = 'Too easy to guess — avoid repeated digits, runs, and years.';
